import { action, computed, observable, runInAction, when } from 'mobx'
import * as tus from 'tus-js-client'
import { TUS_OPTIONS, UPLOAD_ALLOWED_ORIGINS, UPLOAD_WATERMARK } from '@/constants/uploads.js'

export default class UploadModel {
  rootStore = null

  #file = null
  #upload = null

  @observable accessor isUploading = false
  @observable accessor isPaused = false
  @observable accessor isError = false
  @observable accessor isAborted = false
  @observable accessor progress = 0
  @observable accessor videoId = ''

  constructor(rootStore, file) {
    this.rootStore = rootStore
    this.#file = file

    window.addEventListener('beforeunload', this.abort, { once: true })
  }

  get api() {
    return this.rootStore.apiStore
  }

  get auth() {
    return this.rootStore.authStore
  }

  @computed
  get isUploaded() {
    return this.isUploading === false && this.progress === 100
  }

  @action
  reset = async (forceDelete = false) => {
    await this.abort(forceDelete)

    this.#file = null
    this.#upload = null
    this.isUploading = false
    this.isPaused = false
    this.isError = false
    this.isAborted = false
    this.progress = 0
    this.videoId = ''
  }

  @action
  start = () => {
    this.#upload = new tus.Upload(this.#file, {
      ...TUS_OPTIONS,
      endpoint: `${import.meta.env.VITE_APP_BACKEND_URL}/get-upload-url?address=${this.auth.user.address}&signature=${this.auth.signature}&type=upload`,
      metadata: {
        allowedorigins: UPLOAD_ALLOWED_ORIGINS,
        filename: this.#file.name,
        filetype: this.#file.type,
        watermark: UPLOAD_WATERMARK,
      },
      onAfterResponse: this.#onAfterResponse,
      onError: this.#onError,
      onProgress: this.#onProgress,
      onSuccess: this.#onSuccess,
    })

    this.isUploading = true
    this.#upload.start()
  }

  abort = async (forceDelete = false) => {
    await this.#upload?.abort(false)
    await this.#deleteUpload(forceDelete)

    runInAction(() => {
      this.isUploading = false
      this.isPaused = false
      this.isAborted = true
    })
  }

  pause = async () => {
    this.isPaused = true
    await this.#upload?.abort(false)
  }

  resume = () => {
    if (this.isAborted) return

    this.isPaused = false
    this.#upload?.start()
  }

  waitUntilReady = async () =>
    when(() => this.isUploaded === true || this.isError === true || this.isAborted === true)

  @action
  #onAfterResponse = (req, res) =>
    new Promise(resolve => {
      const mediaIdHeader = res.getHeader('stream-media-id')
      if (mediaIdHeader)
        runInAction(() => {
          this.videoId = mediaIdHeader
        })

      resolve()
    })

  @action
  #onError = e => {
    console.log('[Upload] Error', e)
    this.isError = true
    this.isUploading = false
    this.isPaused = false
  }

  @action
  #onProgress = (sent, total) => {
    this.progress = Math.max(Math.min(Math.floor((sent / total) * 100), 100), 0)
  }

  @action
  #onSuccess = () => {
    this.progress = 100
    this.isUploading = false
    this.isPaused = false
    this.isError = false
  }

  #deleteUpload = async forceDelete => {
    if (this.videoId && (!this.isUploaded || forceDelete))
      return this.api.deleteMedia(this.videoId, 'video')
  }
}
